import React, { useState, useEffect } from 'react';
import ComplaintDetailsTab from './viewComplaints/ViewComplaintsModal/ComplaintDetailsTab.jsx';
import AssignmentActionsTab from './viewComplaints/ViewComplaintsModal/AssignmentActionsTab';
import ResolutionDetailsTab from './viewComplaints/ViewComplaintsModal/ResolutionDetailsTab';
import ComplaintActivitiesTab from './viewComplaints/ViewComplaintsModal/ComplaintActivitiesTab';
import Modal from '../../common/Modal';
import { useFetch } from '../../../hooks/useCallApi';
import { Loader2 } from 'lucide-react';

const ViewComplaintModal = ({
  isOpen,
  onClose,
  complaint: initialComplaint,
  getStatusColor,
  getPriorityColor,
  onUpdateStatus,
  onDelete
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [complaintData, setComplaintData] = useState(initialComplaint);

  useEffect(() => {
    if (initialComplaint) {
      console.log('📋 COMPLETE INITIAL COMPLAINT DATASET:', JSON.stringify(initialComplaint, null, 2));
    }
  }, [initialComplaint]);

const {
  isLoading,
  error,
  refetch
} = useFetch(
  ['complaint', initialComplaint?.id],
  (api) => api.admin.complaints.getById(initialComplaint?.id),
  {
    enabled: isOpen && !!initialComplaint?.id, 
    keepPreviousData: false,
    staleTime: 0,
    onSuccess: (response) => {
      console.log('✅ COMPLETE API RESPONSE DATASET:', JSON.stringify(response, null, 2));
      
      const complaint = response?.data?.data || response?.data || response;
      
      console.log('✅ COMPLETE EXTRACTED COMPLAINT DATASET:', complaint);
      
      setComplaintData(complaint);
    },
    onError: (err) => {
      console.error('❌ Error fetching complaint details:', err);
    },
  },
);

  useEffect(() => {
    if (initialComplaint) {
      setComplaintData(initialComplaint);
    }
  }, [initialComplaint]);

  useEffect(() => {
    if (complaintData) {
      console.log('📊 CURRENT COMPLAINT DATA STATE:', JSON.stringify(complaintData, null, 2));
    }
  }, [complaintData]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab(0);
    }
  }, [isOpen]);

  if (!isOpen || !complaintData) {
    return null;
  }

  const formatComplaintType = (type) => {
    if (!type) return 'N/A';
    const typeMap = {
      'broken_streetlights': 'Broken streetlights',
      'garbage_collection': 'Garbage collection',
      'drainage_flooding': 'Drainage / flooding',
      'road_damage': 'Road damage',
      'noise_disturbance': 'Noise disturbance',
      'public_facility_issues': 'Public facility issues'
    };
    return typeMap[type] || type.replace(/_/g, ' ') || 'N/A';
  };

  const formatCategory = (category) => {
    if (!category) return 'N/A';
    if (typeof category === 'string') {
      const categoryMap = {
        'sanitation': 'Sanitation',
        'utilities': 'Utilities',
        'infrastructure': 'Infrastructure',
        'safety': 'Safety'
      };
      return categoryMap[category] || category;
    }
    if (typeof category === 'object') {
      return category.name || 'Uncategorized';
    }
    return 'Uncategorized';
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    const statusMap = {
      'submitted': 'Submitted',
      'acknowledged': 'Acknowledged',
      'assigned': 'Assigned',
      'in progress': 'In Progress',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return statusMap[status] || status;
  };

  const getComplaintRefNo = () => {
    return complaintData.complaint_ref_no || 
           complaintData.reference_no || 
           `CMP-${complaintData.id || '0000'}`;
  };

  const tabs = [
    {
      id: 0,
      title: 'Complaint Details',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      ),
      component: ComplaintDetailsTab
    },
    {
      id: 1,
      title: 'Assignment and Actions',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0 0 15 0m-15 0a7.5 7.5 0 1 1 15 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077 1.41-.513m14.095-5.13 1.41-.513M5.106 17.785l1.15-.964m11.49-9.642 1.149-.964M7.501 19.795l.75-1.3m7.5-12.99.75-1.3m-6.063 16.658.26-1.477m2.605-14.772.26-1.477m0 17.726-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205 12 12m6.894 5.785-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495" />
        </svg>
      ),
      component: AssignmentActionsTab
    },
    {
      id: 2,
      title: 'Resolution Details',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 15h2.25m8.024-9.75c.191.219.5.75 1.072 1.353.318.336.664.683.974 1.045a12.007 12.007 0 0 1-2.077 2.277M16.5 15l-6-6m6 6v3.75m0-3.75h-3.75m6-6h-3.75m-7.5 9h-3a2.25 2.25 0 0 1-2.25-2.25V6A2.25 2.25 0 0 1 6 3.75h7.5A2.25 2.25 0 0 1 15.75 6v.75" />
        </svg>
      ),
      component: ResolutionDetailsTab
    },
    {
      id: 3,
      title: 'Complaint Activities',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      ),
      component: ComplaintActivitiesTab
    }
  ];

  const ActiveComponent = tabs[activeTab].component;

  if (isLoading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        alignment="top"
        title={
          <div className="flex items-center">
            <span className="text-xl font-medium text-gray-900">
              Loading Complaint Details
            </span>
          </div>
        }
        showCloseButton={true}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Fetching complaint information...</p>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        alignment="top"
        title={
          <div className="flex items-center">
            <span className="text-xl font-medium text-gray-900">
              Error Loading Complaint
            </span>
          </div>
        }
        showCloseButton={true}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-gray-600 mb-4">Failed to load complaint details.</p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      alignment="top"
      title={
        <div className="flex items-center">
          <span 
            className="text-md font-medium text-white px-3 py-1.5 rounded-lg mr-2"
            style={{
              background: 'linear-gradient(90deg, #60A4F9 0%, #2865EC 100%)'
            }}
          >
            {getComplaintRefNo()}
          </span>
          <span className="text-xl font-medium text-gray-900">
            Complaint Details
          </span>
        </div>
      }
      showCloseButton={true}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center px-1 py-2 border-b-2 font-medium text-sm transition-all duration-200
                    ${isActive 
                      ? 'border-blue-500' 
                      : 'border-transparent hover:border-gray-300'
                    }
                  `}
                >
                  <div className={`
                    flex items-center justify-center p-1.5 rounded-full transition-all duration-200 mr-2
                    ${isActive 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                    }
                  `}>
                    {tab.icon}
                  </div>
                  <span className={`
                    ${isActive ? 'text-blue-600 font-semibold' : 'text-gray-500 group-hover:text-gray-700'}
                  `}>
                    {tab.title}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="animate-in fade-in duration-300">
          <ActiveComponent
            complaint={complaintData}
            getStatusColor={getStatusColor}
            getPriorityColor={getPriorityColor}
            formatComplaintType={formatComplaintType}
            formatCategory={formatCategory}
            formatStatus={formatStatus}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ViewComplaintModal;